import React, { useEffect, useRef } from 'react';
import Modal from '@vromo/ui/components/Modal';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import Alert from '@vromo/ui/components/Alert';

import Input from '@vromo/ui/components/Input';
import Logo from '@vromo/icons/logo';

import { API } from '@vromo/api/api';
import FontAwesome from '@vromo/ui/components/FontAwesome';

import theme from '@vromo/ui/components/theme';
import { darken } from 'polished';

import styled from '@emotion/styled';
import { css } from 'emotion';
import { useLocalObservable } from 'mobx-react-lite';
import useAuth from '../useAuth';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { qaLabelLogin, qaLabelResetPasswordModal } from '../../../../common-qa-labels/login';
import { useTranslation } from 'react-i18next';
import { bookVromoDemoUrl } from '@vromo/api/constants';

const modalStyle = {
    overlay: {
        background: 'rgba(0,0,0,0.4)'
    },
    content: {
        background: 'white',
        borderRadius: '8px',
        padding: 40,
        border: 'none',
        bottom: 'auto',
        top: 'auto'
    }
};

const Button = styled.button`
    width: 100%;
    border: none;
    margin-top: 25px;
    background: ${theme.primary};
    border-radius: 4px;
    font-size: 17px;
    color: #ffffff;
    text-align: center;
    padding: 8px 16px;
    transition: background-color 0.2s ease-in-out;

    &:hover {
        background: ${darken(0.12, theme.primary)};
    }
`;

interface Props {
    location: any;
}

export default observer<Props>(function LoginForm({ location }) {
    const passwordRef = useRef<HTMLInputElement>(null);
    const store = useLocalObservable(() => ({
        forgotPassword: false,

        email: '',
        password: '',

        errors: [] as string[],
        message: '',
        loading: false,
        passwordResetEmail: '',
        redirectToNext: false
    }));

    const [login] = useAuth();

    const { t } = useTranslation();

    useEffect(() => {
        const { search } = location;

        const query = queryString.parse(search);

        try {
            localStorage.setItem('storageTest', 'stored');
            localStorage.removeItem('storageTest');
        } catch (it) {
            runInAction(
                () =>
                    (store.errors = [
                        t("login.privateBrowsingMode")
                    ])
            );
        }

        if (query.username) {
            runInAction(() => (store.email = query.username as string));
            passwordRef.current?.focus();
        }
    }, []);

    async function resetPassword(e: React.SyntheticEvent) {
        e.preventDefault();

        runInAction(() => (store.loading = true));

        const email = store.passwordResetEmail;

        new API().resetPassword
            .post({
                email,
                host: window.location.hostname
            })
            .catch((e) => {
                console.warn(e);
            })
            .finally(() => {
                runInAction(() => {
                    store.loading = false;
                    store.forgotPassword = false;
                    store.message = t("login.resetPasswordLink", { email });
                });
            });
    }

    async function submit(e: React.SyntheticEvent) {
        e.preventDefault();

        async function makeToken(username: string, password: string): Promise<string | null> {
            try {
                const result = await new API().auth.post({ name: username, pass: password });
                return result.success ? result.token : null;
            } catch (error) {
                return null;
            }
        }

        try {
            runInAction(() => (store.loading = true));
            const token = await makeToken(store.email, store.password);

            if (token) {
                runInAction(() => (store.loading = true));

                login(token)
                    .then(() => runInAction(() => (store.redirectToNext = true)))
                    .catch((e: Error) => {
                        console.error(e);
                        runInAction(() => (store.errors = [e.message]));
                    });
            } else {
                runInAction(
                    () => (store.errors = [t("login.invalidCredentials")])
                );
            }
        } catch (ex) {
            runInAction(
                () =>
                    (store.errors = [
                        t("login.failedConnection")
                    ])
            );
        }

        runInAction(() => (store.loading = false));
    }

    if (store.redirectToNext) {
        const { from } = location.state || { from: { pathname: '/' } };
        return <Redirect to={from} />;
    }

    const errorDisplay = store.errors.map((error, ix) => (
        <Alert data-testid={qaLabelLogin.errorMessage} key={ix} danger style={{ marginBottom: 30 }}>
            {error}
        </Alert>
    ));

    return (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#F4F4F4'
            }}>
            <div style={{ backgroundColor: '#fff' }}>
                <div
                    className={css`
                        display: flex;
                        justify-content: space-between;
                        padding: 20px 40px;

                        @media (max-width: 700px) {
                            justify-content: center;
                        }
                    `}>
                    <Logo style={{ height: 35, marginBottom: 8 }} />

                    <div
                        className={css`
                            @media (max-width: 700px) {
                                display: none;
                            }
                        `}>
                        <p style={{ fontSize: '16px' }}>
                            {t("login.notCustomer")}
                            <a href={ bookVromoDemoUrl }> {t("login.bookDemo")}</a>
                        </p>
                    </div>
                </div>

                <h1 style={{ textAlign: 'center', margin: 40, marginTop: 20 }} data-testid={qaLabelLogin.signInTitle}>{t("login.signIn")}</h1>
            </div>

            <div
                className={css`
                    display: flex;
                    padding: 2.4rem;
                    justify-content: center;
                    align-content: flex-start;

                    @media (max-width: 700px) {
                        padding: 1.2rem;
                    }
                `}>
                <div
                    className={css`
                        width: 35rem;
                        padding: 2.4rem;
                        background: #ffffff;
                        box-shadow: 0 5px 16px 0 rgba(0, 0, 0, 0.19);
                        border-radius: 8px;
                    `}
                    style={{ padding: 40 }}>
                    <form
                        onSubmit={submit}
                        className={css`
                            display: flex;
                            flex-direction: column;
                            align-items: stretch;
                        `}
                        >
                        {errorDisplay}
                        {store.message && <Alert success data-testid={qaLabelLogin.resetPassValidationMessage}>{store.message}</Alert>}
                        <div>
                            <label style={{ fontWeight: 400, fontSize: '1.1em' }}>{t("login.email")}</label>
                            <Input
                                type="text"
                                name="user"
                                placeholder={t("login.emailPlaceholder")}
                                value={store.email}
                                onChange={(e) => runInAction(() => (store.email = e.target.value))}
                                required
                                style={{ marginBottom: 8 }}
                                data-testid={qaLabelLogin.emailInput}
                            />
                        </div>

                        <div style={{ marginTop: '20px' }}>
                            <label style={{ fontWeight: 400, fontSize: '1.1em' }}>{t("login.password")}</label>
                            <Input
                                type="password"
                                name="password"
                                placeholder="Password"
                                onChange={(e) =>
                                    runInAction(() => (store.password = e.target.value))
                                }
                                ref={passwordRef}
                                required
                                style={{ marginBottom: 8 }}
                                data-testid={qaLabelLogin.passwordInput}
                            />
                            <a
                                onClick={() => runInAction(() => (store.forgotPassword = true))}
                                style={{ color: theme.primary }}
                                data-testid={qaLabelLogin.forgotPasswordLink}>
                                {t("login.forgotPassword")}
                            </a>
                        </div>

                        <Button data-testid={qaLabelLogin.loginButton}>
                            {store.loading && !store.forgotPassword ? (
                                <FontAwesome icon="spinner" spin />
                            ) : (
                                <span>Sign in</span>
                            )}
                        </Button>
                    </form>
                </div>
            </div>

            <Modal

                contentLabel="Password reset"
                style={modalStyle}
                onRequestClose={() =>
                    runInAction(() => {
                        store.forgotPassword = false;
                        store.passwordResetEmail = '';
                    })
                }
                isOpen={store.forgotPassword}>
                <form onSubmit={resetPassword}>
                    <h1 style={{ marginTop: 0, textAlign: 'center' }} data-testid={qaLabelResetPasswordModal.title}>{t("login.passwordReset")}</h1>
                    <hr />
                    <h3 style={{ fontWeight: 300, textAlign: 'center' }} data-testid={qaLabelResetPasswordModal.subtitle}>
                        {t("login.enterEmail")}
                    </h3>
                    <label style={{ fontWeight: 300, fontSize: '1.1em' }}>{t("login.email")}</label>
                    <Input
                        type="email"
                        data-testid={qaLabelResetPasswordModal.emailInput}
                        value={store.passwordResetEmail}
                        onChange={(e) =>
                            runInAction(() => (store.passwordResetEmail = e.target.value))
                        }
                        placeholder={t("login.emailPlaceholder")}
                        required
                    />

                    <Button disabled={store.loading} type="submit" data-testid={qaLabelResetPasswordModal.submitButton}>
                        {store.loading && store.forgotPassword ? (
                            <FontAwesome icon="spinner" spin={true} />
                        ) : (
                            t("login.submitButton")
                        )}
                    </Button>
                </form>
            </Modal>
        </div>
    );
});
